import { hasPageBreak } from '@efdigital/ll-addon-content/utils/quiz-css-class';
import type { Quiz, QuizQuestion } from '@efdigital/ll-addon-data';
import { concat } from '@ember/helper';
import { uniqueId } from '@ember/helper';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

import { type EditRecordSignature } from '../../edit-record-base.gts';
import { EditRecordCollapse } from '../../edit-record-collapse.gts';
import QuestionEditorButtons from './output-question-editor-buttons.gts';
import QuestionEditorCards from './question-editor-cards.gts';
import {
  addOne,
  type EditingMode,
  eq,
  getPageNumber,
  hasAnyPageBreaks,
  lookupQuestionManager,
} from './utils.ts';

export class EditQuizQuestionsOutput extends Component<
  EditRecordSignature<
    Quiz,
    { onQuestionAdded: (newQuestion: QuizQuestion) => void }
  >
> {
  @service declare store: Store;

  @tracked selectedIndex = -1;
  @tracked editingMode: EditingMode = 'inactive';

  readonly uniqueId = uniqueId();

  onEdit = () => {
    this.selectedIndex = -1;
    this.editingMode = 'inactive';
  };

  setEditingMode = (
    question: QuizQuestion | null,
    index: number,
    mode: EditingMode
  ) => {
    if (question) {
      const manager = lookupQuestionManager(question);
      const defaultQuestionData = manager.defaultData;

      const hasMissingData = Object.keys(defaultQuestionData).some((k) => {
        const key = k as keyof QuizQuestion;
        return (
          !(key in question) ||
          typeof question[key] === 'undefined' ||
          question[key] === null
        );
      });

      if (hasMissingData) {
        Object.assign(question, structuredClone(defaultQuestionData));
      }

      this.scrollToQuestion(question);
    }
    this.selectedIndex = index;
    this.editingMode = mode;
  };

  isEditing = (index: number) => {
    return this.selectedIndex === index;
  };

  isEditingMode = (index: number, mode: EditingMode) => {
    return this.selectedIndex === index && this.editingMode === mode;
  };

  shouldShowPageIndicator = (
    question: QuizQuestion,
    index: number,
    allQuestions: QuizQuestion[]
  ): boolean => {
    const questionHasPageBreak = hasPageBreak(question.css || '');
    const isFirstQuestion = index === 0;

    // Show indicator if:
    // 1. Question has page-break AND it's not the first question
    // 2. OR it's the first question AND there are any page breaks in the quiz
    if (questionHasPageBreak) {
      return !isFirstQuestion;
    } else {
      return isFirstQuestion && hasAnyPageBreaks(allQuestions);
    }
  };

  scrollToQuestion = (question: QuizQuestion) => {
    // TODO: Remove this once we have a better way to scroll to the question
    // replacing `next` with `runTask` from ember-lifeline
    /* eslint-disable-next-line ember/no-runloop */
    next(() => {
      const questionElement = document.getElementById(
        `${this.uniqueId}-question-${question.qid}`
      );
      if (questionElement) {
        questionElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    });
  };

  <template>
    <EditRecordCollapse
      @editLabel="Edit Answers"
      @item={{@item}}
      @context={{@context}}
      @onEdit={{this.onEdit}}
      @maxDepth={{2}}
      @collapseLabel="Answers"
      ...attributes
    >
      <:help>
        <p class="form-text">
          Specify the correct answers for each question and configure scoring
          and feedback based on the user's answers.
        </p>
      </:help>
      <:form as |uniqueId isDisabled isReadOnly|>
        {{#if isReadOnly}}
          {{#if @item.questions.length}}
            <div class="list-group mb-3">
              {{#each @item.questions as |question index|}}
                {{#let (lookupQuestionManager question) as |questionManager|}}
                  {{#if
                    (this.shouldShowPageIndicator
                      question index @item.questions
                    )
                  }}
                    <div
                      class="d-flex align-items-center gap-2 px-2 pt-2 bg-primary-subtle text-primary rounded mb-1"
                    >
                      <iconify-icon inline icon="ph:file-dotted" class="fs-5" />
                      <span class="fw-semibold">
                        Page
                        {{getPageNumber @item.questions index}}:
                        {{#if question.title}}
                          {{question.title}}
                        {{else}}
                          <em class="text-muted">(Untitled)</em>
                        {{/if}}
                      </span>
                    </div>
                  {{/if}}

                  {{#if questionManager}}
                    <div class="list-group-item">
                      <iconify-icon inline icon={{questionManager.icon}} />
                      {{if
                        question.title
                        question.title
                        (concat "(Untitled) " questionManager.label)
                      }}{{#if question.required}}<span
                          class="text-danger fw-bold ms-1"
                        >*</span>{{/if}}
                    </div>
                  {{else}}
                    <div class="list-group-item">
                      <iconify-icon inline icon="ph:question-bold" />
                      (Unknown question type)
                    </div>
                  {{/if}}
                {{/let}}
              {{/each}}
            </div>
          {{else}}
            <div class="alert alert-info">
              No questions have been created yet. Please add some questions in
              the "Input Form" first.
            </div>
          {{/if}}
        {{else}}
          <fieldset disabled={{isDisabled}} class="mb-0">
            {{#if @item.questions.length}}
              <div class="list-group mb-3 gap-1">
                {{#each @item.questions as |question index|}}
                  {{#let (lookupQuestionManager question) as |manager|}}
                    {{#let (eq this.selectedIndex index) as |isEditing|}}
                      {{#if
                        (this.shouldShowPageIndicator
                          question index @item.questions
                        )
                      }}
                        <div
                          class="d-flex align-items-center gap-2 px-2 pt-2 bg-primary-subtle text-primary rounded mb-1"
                        >
                          <iconify-icon
                            inline
                            icon="ph:file-dotted"
                            class="fs-5"
                          />
                          <span class="fw-semibold">
                            Page
                            {{getPageNumber @item.questions index}}:
                            {{#if question.title}}
                              {{question.title}}
                            {{else}}
                              <em class="text-muted">(Untitled)</em>
                            {{/if}}
                          </span>
                        </div>
                      {{/if}}

                      <div
                        class="question-item-container
                          {{if isEditing 'is-editing shadow rounded-bottom-1'}}"
                        id="{{this.uniqueId}}-question-{{question.qid}}"
                        tabindex="0"
                      >
                        <div
                          class="d-flex flex-row align-items-stretch position-relative"
                        >
                          <span
                            class="input-group-text justify-content-center drag-handle w-3rem rounded-end-0 border-end-0 d-flex flex-column align-items-center
                              {{if isEditing 'rounded-bottom-0'}}"
                          >
                            <iconify-icon
                              inline
                              icon={{manager.icon}}
                              class="my-1 fs-4"
                            />
                            <div class="text-center mb-auto">
                              {{addOne index}}{{#if question.required}}<span
                                  class="text-danger fw-bold"
                                >*</span>{{/if}}
                            </div>
                          </span>

                          <div class="d-flex flex-column flex-grow-1">
                            <span
                              id="{{uniqueId}}-question-title-{{question.qid}}"
                              class="form-control list-group-item rounded-start-0 rounded-bottom-0 rounded-top-1"
                            >{{question.title}}</span>

                            <QuestionEditorButtons
                              @question={{question}}
                              @index={{index}}
                              @isEditing={{isEditing}}
                              @manager={{manager}}
                              @isEditingMode={{this.isEditingMode}}
                              @setEditingMode={{this.setEditingMode}}
                            />
                          </div>
                        </div>

                        <QuestionEditorCards
                          @question={{question}}
                          @index={{index}}
                          @manager={{manager}}
                          @quiz={{@item}}
                          @isEditingMode={{this.isEditingMode}}
                        />
                      </div>
                    {{/let}}
                  {{/let}}
                {{/each}}
              </div>
            {{else}}
              <div class="alert alert-info">
                No questions have been created yet. Please add some questions in
                the "Input Form" first.
              </div>
            {{/if}}
          </fieldset>
        {{/if}}
      </:form>
    </EditRecordCollapse>
  </template>
}
